import React, { useState, useRef, useCallback, useEffect, memo } from 'react';

interface LongPressRevealButtonProps {
  /** Icon to show in the tab and confirmation */
  icon: React.ReactNode;
  /** Label shown in the brief confirmation */
  label: string;
  /** Callback fired immediately on long-press */
  onAction: () => void;
  /** Which side of the screen: 'left' or 'right' */
  side: 'left' | 'right';
  /** Whether the tab should be visible. The confirmation stays visible
   *  even after this becomes false (e.g. when onAction clears previousWord). */
  visible?: boolean;
  /** Additional class names */
  className?: string;
}

/** How long (ms) the user must hold the tab to trigger the action */
const LONG_PRESS_MS = 400;
/** How long (ms) the confirmation message stays visible */
const CONFIRMATION_MS = 1500;

/**
 * A small tab pinned to the bottom corner. Long-press (~400ms) fires the
 * action immediately and shows a brief confirmation that auto-dismisses.
 * One step — no second tap needed. Designed to be invisible to kids while
 * accessible to parents.
 *
 * Use the `visible` prop to control when the tab appears. The confirmation
 * will remain visible even if `visible` becomes false after the action fires.
 *
 * @example
 * <LongPressRevealButton
 *   icon={<RotateCcw size={16} />}
 *   label="Needs Practice"
 *   onAction={handlePreviousNeedsPractice}
 *   side="left"
 *   visible={!!previousWord}
 * />
 */
export const LongPressRevealButton: React.FC<LongPressRevealButtonProps> = memo(({
  icon,
  label,
  onAction,
  side,
  visible = true,
  className = '',
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  }, []);

  // Clean up on unmount
  useEffect(() => clearTimers, [clearTimers]);

  const handlePressStart = useCallback(() => {
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      // Fire the action immediately
      onAction();
      // Show brief confirmation
      setShowConfirmation(true);
      confirmTimerRef.current = setTimeout(() => {
        setShowConfirmation(false);
      }, CONFIRMATION_MS);
    }, LONG_PRESS_MS);
  }, [onAction]);

  const handlePressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Prevent context menu on long-press (mobile)
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Render nothing when not visible and not showing confirmation
  if (!visible && !showConfirmation) {
    return null;
  }

  // Position classes based on side
  const positionClasses = side === 'left'
    ? 'left-0 rounded-r-full'
    : 'right-0 rounded-l-full';

  return (
    <div
      className={`fixed bottom-20 ${positionClasses} z-30 ${className}`}
    >
      {showConfirmation ? (
        // Brief confirmation after action fires
        <div
          className={`
            flex items-center gap-2 animate-pop
            px-4 py-2.5
            bg-green-100 dark:bg-green-900/40
            border border-green-300 dark:border-green-700
            text-green-700 dark:text-green-300
            text-sm font-bold font-heebo
            ${side === 'left' ? 'rounded-r-full pl-3' : 'rounded-l-full pr-3'}
            shadow-md
          `}
        >
          <span className="flex-shrink-0">{icon}</span>
          <span>{label}</span>
          <span>&#10003;</span>
        </div>
      ) : (
        // Default state: tiny tab — long-press to trigger
        <div
          role="button"
          tabIndex={0}
          onMouseDown={handlePressStart}
          onMouseUp={handlePressEnd}
          onMouseLeave={handlePressEnd}
          onTouchStart={handlePressStart}
          onTouchEnd={handlePressEnd}
          onTouchCancel={handlePressEnd}
          onContextMenu={handleContextMenu}
          className={`
            flex items-center justify-center
            w-8 h-10
            bg-gray-200/60 dark:bg-gray-700/40
            text-gray-400 dark:text-gray-500
            ${side === 'left' ? 'rounded-r-lg' : 'rounded-l-lg'}
            select-none
            cursor-pointer
          `}
          aria-label={`Hold to mark previous word as needs practice`}
        >
          <span className="text-xs opacity-60">
            {side === 'left' ? '›' : '‹'}
          </span>
        </div>
      )}
    </div>
  );
});

LongPressRevealButton.displayName = 'LongPressRevealButton';
